import { useLayoutEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData, QueryKey, UseInfiniteQueryOptions } from "@tanstack/react-query";
import type { Page, Post } from "@/api/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useScrollContainer } from "@/components/layout/ScrollContainer";
import { PostCard, PostCardSkeleton } from "@/components/social";

export function VirtualPostList<TQueryKey extends QueryKey>({
  query,
}: {
  query: UseInfiniteQueryOptions<
    Page<Post>,
    Error,
    InfiniteData<Page<Post>>,
    TQueryKey,
    string | null
  >;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(query);
  const posts = data?.pages.flatMap((page) => page.items) ?? [];
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const visiblePosts = posts.filter((post) => !hiddenIds.has(post.id));
  const listRef = useRef<HTMLDivElement>(null);
  const scrollElement = useScrollContainer();
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    const scroller = scrollElement;
    if (!list || !scroller) return;
    const measureList = () => {
      setScrollMargin(
        list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop,
      );
    };
    measureList();
    const observer = new ResizeObserver(measureList);
    observer.observe(list);
    window.addEventListener("resize", measureList);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureList);
    };
  }, [scrollElement]);

  const count = visiblePosts.length + (hasNextPage ? 1 : 0);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollElement,
    estimateSize: () => 420,
    overscan: 6,
    scrollMargin,
    measureElement: (element) => element.getBoundingClientRect().height,
  });
  const virtualItems = virtualizer.getVirtualItems();

  useLayoutEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (
      lastItem &&
      lastItem.index >= count - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  }, [count, fetchNextPage, hasNextPage, isFetchingNextPage, virtualItems]);

  if (!data && posts.length === 0) {
    return (
      <div className="space-y-4" aria-label="Loading feed">
        <PostCardSkeleton variant="media" />
        <PostCardSkeleton />
        <PostCardSkeleton variant="media" />
      </div>
    );
  }

  return (
    <div ref={listRef} className="relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualItems.map((virtualItem) => {
        const isLoader = virtualItem.index >= visiblePosts.length;
        return (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className="absolute left-0 top-0 w-full pb-4"
            style={{ transform: `translateY(${virtualItem.start - scrollMargin}px)` }}
          >
            {isLoader ? (
              <PostCardSkeleton />
            ) : (
              <PostCard
                post={visiblePosts[virtualItem.index]!}
                onHide={(id) => setHiddenIds((current) => new Set(current).add(id))}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
